/**
 * Component: sa-best-sellers
 * Description: Best sellers section with product cards carousel
 */
if (!customElements.get('sa-best-sellers')) {
  class SaBestSellers extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.cartPositions = {}; // Store pre-calculated cart positions
      this.appliedMargins = {}; // Store currently applied margin-top values
      this.initSwiper = this.initSwiper.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
    }

    connectedCallback() {
      const initComponent = () => {
        if (typeof Swiper === 'undefined') {
          setTimeout(() => {
            initComponent();
          }, 100);
          return;
        }
        
        setTimeout(() => {
          this.initSwiper();
          this.initAjaxAddToCart();
          this.preCalculateCartPositions();
        }, 50);
      };

      initComponent();

      // Listen for window resize to recalculate cart positions & reinitialize Swiper
      this.handleResize = this.debounce(() => {
        const isDesktop = window.innerWidth >= 768;
        // Re-initialize Swiper when resizing from mobile back to desktop
        if (isDesktop && !this.swiperInstance) {
          this.initSwiper();
        }
        this.preCalculateCartPositions();
      }, 250);
      window.addEventListener('resize', this.handleResize);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this));
      }
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    handleSectionLoad(event) {
      if (!event || !event.detail) {
        return;
      }
      const sectionId = this.getAttribute('data-section-id');
      if (event.detail.sectionId === sectionId) {
        setTimeout(() => {
          if (this.isConnected) {
            this.initSwiper();
            this.initAjaxAddToCart();
            this.preCalculateCartPositions();
          }
        }, 200);
      }
    }

    disconnectedCallback() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
      if (window.Shopify && window.Shopify.designMode && this.handleSectionLoad) {
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      }
    }

    initAjaxAddToCart() {
      const addToCartButtons = this.querySelectorAll('.sa-best-sellers__add-to-cart');
      if (addToCartButtons.length === 0) {
        return;
      }

      if (typeof jQuery !== 'undefined' && typeof theme !== 'undefined' && theme.settings && theme.settings.cartType === 'drawer') {
        addToCartButtons.forEach((button) => {
          const form = button.closest('.sa-best-sellers__add-to-cart-form');
          if (form && typeof theme.AjaxProduct !== 'undefined') {
            new theme.AjaxProduct(jQuery(form));
          }
        });
      }
    }

    initSwiper() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }

      const swiperContainer = this.querySelector('.sa-best-sellers__wrapper.swiper');
      if (!swiperContainer || typeof Swiper === 'undefined') {
        return;
      }

      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        swiperContainer.classList.add('swiper-initialized');
        return;
      }

      let slidesPerViewDesktopStr = this.getAttribute('data-slides-per-view-desktop');
      if (!slidesPerViewDesktopStr || slidesPerViewDesktopStr.trim() === '') {
        slidesPerViewDesktopStr = '2.6';
      }
      const slidesPerViewDesktop = parseFloat(slidesPerViewDesktopStr) || 2.6;
      
      let slidesPerViewMobileStr = this.getAttribute('data-slides-per-view-mobile');
      const slidesPerViewMobile = (slidesPerViewMobileStr && slidesPerViewMobileStr.trim() !== '') ? parseInt(slidesPerViewMobileStr) : 2;
      
      let spaceBetweenStr = this.getAttribute('data-space-between');
      const spaceBetween = (spaceBetweenStr && spaceBetweenStr.trim() !== '') ? parseInt(spaceBetweenStr) : 20;
      
      const totalSlides = swiperContainer.querySelectorAll('.swiper-slide').length;

      const swiperOptions = {
        loop: false,
        speed: 600,
        slidesPerView: slidesPerViewDesktop,
        spaceBetween: spaceBetween,
        slidesPerGroup: 1,
        grabCursor: true,
        allowTouchMove: true,
        watchOverflow: true,
        navigation: {
          nextEl: this.querySelector('.sa-best-sellers__nav-button--next'),
          prevEl: this.querySelector('.sa-best-sellers__nav-button--prev'),
          hideOnClick: false,
        },
        breakpoints: {
          768: {
            slidesPerView: slidesPerViewDesktop,
            spaceBetween: spaceBetween,
          },
        },
        on: {
          init: (swiper) => {
            swiperContainer.classList.add('swiper-initialized');
            this.updateNavigationVisibility(swiper, totalSlides, slidesPerViewDesktop, slidesPerViewMobile);
            // Pre-calculate positions after swiper is initialized
            setTimeout(() => {
              this.preCalculateCartPositions();
            }, 100);
          },
          resize: (swiper) => {
            const isDesktop = window.innerWidth >= 768;
            if (!isDesktop) {
              swiper.destroy(true, true);
              this.swiperInstance = null;
              swiperContainer.classList.add('swiper-initialized');
              this.applyCartPositions(); // Reset on mobile
              return;
            }
            if (!this.swiperInstance && isDesktop) {
              this.initSwiper();
              return;
            }
            const currentSlidesPerView = isDesktop ? slidesPerViewDesktop : slidesPerViewMobile;
            this.updateNavigationVisibility(swiper, totalSlides, currentSlidesPerView, currentSlidesPerView);
            // Recalculate positions on resize
            setTimeout(() => {
              this.preCalculateCartPositions();
            }, 100);
          },
          slideChange: (swiper) => {
            // Calculate positions for newly visible slides
            setTimeout(() => {
              this.calculateVisibleSlides(swiper);
              this.applyCartPositions();
            }, 650); // Wait for slide animation to complete (speed: 600ms + buffer)
          },
          slideChangeTransitionEnd: (swiper) => {
            // Calculate and apply positions when transition ends
            this.calculateVisibleSlides(swiper);
            this.applyCartPositions();
          },
          transitionEnd: (swiper) => {
            // Calculate and apply positions after any transition ends
            this.calculateVisibleSlides(swiper);
            this.applyCartPositions();
          },
        },
      };

      // Autoplay config from data attributes
      const autoplayEnabledAttr = this.getAttribute('data-autoplay');
      const autoplayEnabled = autoplayEnabledAttr === 'true' || autoplayEnabledAttr === '1';
      const autoplayDelayStr = this.getAttribute('data-autoplay-delay');
      const autoplayDelay = (autoplayDelayStr && autoplayDelayStr.trim() !== '') ? parseInt(autoplayDelayStr) : 3000;

      if (autoplayEnabled) {
        swiperOptions.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        };
      }

      this.swiperInstance = new Swiper(swiperContainer, swiperOptions);
    }

    updateNavigationVisibility(swiper, totalSlides, slidesPerViewDesktop, slidesPerViewMobile) {
      const isDesktop = window.innerWidth >= 768;
      const currentSlidesPerView = isDesktop ? slidesPerViewDesktop : slidesPerViewMobile;
      const needsNavigation = totalSlides > Math.ceil(currentSlidesPerView);

      const nextBtn = this.querySelector('.sa-best-sellers__nav-button--next');
      const prevBtn = this.querySelector('.sa-best-sellers__nav-button--prev');

      if (nextBtn && prevBtn) {
        if (needsNavigation) {
          nextBtn.style.display = '';
          prevBtn.style.display = '';
        } else {
          nextBtn.style.display = 'none';
          prevBtn.style.display = 'none';
        }
      }
    }

    /**
     * Pre-calculate cart positions for all cards (including hidden ones)
     * This runs asynchronously to avoid blocking the UI
     */
    preCalculateCartPositions() {
/*      // Only calculate on desktop, mobile uses different layout
      if (window.innerWidth < 768) {
        this.cartPositions = {};
        this.applyCartPositions(); // Reset on mobile
        return;
      }

      // Clear previous positions
      this.cartPositions = {};

      // Wait for DOM to be ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Get all slides (including hidden ones)
          const slides = this.querySelectorAll('.swiper-slide');
          
          if (slides.length === 0) {
            // Fallback: get cards directly if swiper not initialized
            const cards = this.querySelectorAll('.sa-best-sellers__card');
            this.calculateCardPositions(cards);
            return;
          }

          // Calculate positions for each slide
          this.calculateCardPositions(slides);
        });
      });*/
    }

    /**
     * Calculate positions for a set of cards/slides
     */
    calculateCardPositions(elements) {
      elements.forEach((slide, index) => {
        const card = slide.querySelector('.sa-best-sellers__card');
        if (!card) return;

        const contentLeft = card.querySelector('.sa-best-sellers__content-left');
        const contentRight = card.querySelector('.sa-best-sellers__content-right');
        const priceElement = contentLeft ? contentLeft.querySelector('.sa-best-sellers__price') : null;
        const cartWrapper = contentRight ? contentRight.querySelector('.sa-best-sellers__add-to-cart-wrapper') : null;

        if (!priceElement || !cartWrapper || !contentLeft || !contentRight) {
          return;
        }

        // Check if element is currently visible
        const isVisible = slide.offsetParent !== null && 
                          !slide.classList.contains('swiper-slide-duplicate') &&
                          window.getComputedStyle(slide).display !== 'none';

        // For hidden slides, we'll calculate when they become visible
        // For now, calculate visible ones immediately
        if (isVisible) {
          this.calculateSingleCardPosition(card, contentLeft, contentRight, priceElement, cartWrapper, index);
        } else {
          // For hidden cards, store a flag to calculate when visible
          // We'll use a MutationObserver or calculate on slideChange
          this.cartPositions[index] = null; // Mark as pending
        }
      });

      // After initial calculation, apply to visible cards
      setTimeout(() => {
        this.applyCartPositions();
      }, 50);
    }

    /**
     * Calculate position for a single card
     */
    calculateSingleCardPosition(card, contentLeft, contentRight, priceElement, cartWrapper, index) {
      // Get bounding rectangles
      const contentLeftRect = contentLeft.getBoundingClientRect();
      const contentRightRect = contentRight.getBoundingClientRect();
      const priceRect = priceElement.getBoundingClientRect();
      const cartRect = cartWrapper.getBoundingClientRect();
      
      // Calculate distance from price bottom to content-left bottom
      const priceBottom = priceRect.bottom;
      const contentLeftBottom = contentLeftRect.bottom;
      const priceDistanceFromBottom = contentLeftBottom - priceBottom;
      
      // Calculate cart height
      const cartHeight = cartRect.height;
      
      // Calculate current cart position relative to content-right top
      const cartTop = cartRect.top;
      const contentRightTop = contentRightRect.top;
      const currentCartTopFromContentRightTop = cartTop - contentRightTop;
      
      // Calculate desired cart position
      // We want cart bottom to align with price bottom
      // Since content-left and content-right have same height (flex stretch)
      // cart should be at: content-right bottom - cart height - priceDistanceFromBottom
      const contentRightHeight = contentRightRect.height;
      const desiredCartTopFromContentRightTop = contentRightHeight - cartHeight - priceDistanceFromBottom;
      
      // Calculate margin-top adjustment
      const marginTop = desiredCartTopFromContentRightTop - currentCartTopFromContentRightTop;

      // Store the calculated position
      this.cartPositions[index] = marginTop;
    }

    /**
     * Calculate positions for currently visible slides
     */
    calculateVisibleSlides(swiper) {
      if (!swiper || window.innerWidth < 768) {
        return;
      }

      const slides = swiper.slides || this.querySelectorAll('.swiper-slide');
      
      slides.forEach((slide, index) => {
        // Check if slide is currently visible (not duplicate and in viewport)
        const isVisible = slide.classList.contains('swiper-slide-active') ||
                         slide.classList.contains('swiper-slide-visible') ||
                         (slide.offsetParent !== null && !slide.classList.contains('swiper-slide-duplicate'));

        if (!isVisible) {
          return;
        }

        // If position not calculated yet, calculate it now
        if (this.cartPositions[index] === null || this.cartPositions[index] === undefined) {
          const card = slide.querySelector('.sa-best-sellers__card');
          if (!card) return;

          const contentLeft = card.querySelector('.sa-best-sellers__content-left');
          const contentRight = card.querySelector('.sa-best-sellers__content-right');
          const priceElement = contentLeft ? contentLeft.querySelector('.sa-best-sellers__price') : null;
          const cartWrapper = contentRight ? contentRight.querySelector('.sa-best-sellers__add-to-cart-wrapper') : null;

          if (priceElement && cartWrapper && contentLeft && contentRight) {
            this.calculateSingleCardPosition(card, contentLeft, contentRight, priceElement, cartWrapper, index);
          }
        }
      });
    }

    /**
     * Apply pre-calculated cart positions to visible cards
     */
    applyCartPositions() {
      // Only apply on desktop
      if (window.innerWidth < 768) {
        // Reset margin-top on mobile
        const cartWrappers = this.querySelectorAll('.sa-best-sellers__add-to-cart-wrapper');
        cartWrappers.forEach(wrapper => {
          wrapper.style.marginTop = '';
        });
        this.appliedMargins = {};
        return;
      }

      // Get all slides
      const slides = this.querySelectorAll('.swiper-slide');
      if (slides.length === 0) {
        // Fallback: get cards directly
        const cards = this.querySelectorAll('.sa-best-sellers__card');
        cards.forEach((card, index) => {
          const contentRight = card.querySelector('.sa-best-sellers__content-right');
          const cartWrapper = contentRight ? contentRight.querySelector('.sa-best-sellers__add-to-cart-wrapper') : null;
          if (cartWrapper && this.cartPositions.hasOwnProperty(index) && this.cartPositions[index] !== null) {
            const newMargin = this.cartPositions[index];
            const currentMargin = this.appliedMargins[index] || 0;
            
            // Only update if there's a significant change (more than 1px)
            if (Math.abs(newMargin - currentMargin) > 1) {
              cartWrapper.style.marginTop = `${newMargin}px`;
              this.appliedMargins[index] = newMargin;
            }
          }
        });
        return;
      }

      // Apply to slides
      slides.forEach((slide, index) => {
        // Skip duplicate slides
        if (slide.classList.contains('swiper-slide-duplicate')) {
          return;
        }

        const card = slide.querySelector('.sa-best-sellers__card');
        if (!card) return;

        const contentRight = card.querySelector('.sa-best-sellers__content-right');
        const cartWrapper = contentRight ? contentRight.querySelector('.sa-best-sellers__add-to-cart-wrapper') : null;

        if (!cartWrapper) {
          return;
        }

        // Apply pre-calculated position if available
        if (this.cartPositions.hasOwnProperty(index) && this.cartPositions[index] !== null) {
          const newMargin = this.cartPositions[index];
          const currentMargin = this.appliedMargins[index] || 0;
          
          // Only update if there's a significant change (more than 1px)
          if (Math.abs(newMargin - currentMargin) > 1) {
            cartWrapper.style.marginTop = `${newMargin}px`;
            this.appliedMargins[index] = newMargin;
          }
        }
      });
    }

  }

  customElements.define('sa-best-sellers', SaBestSellers);
}
